import React, { useState, useEffect } from 'react';
import { Building2, MapPin, Phone, Mail, Globe, Star, Shield, CreditCard, Plus, Save, X, TestTube, Zap } from 'lucide-react';
import MasterDataService from '../../services/masterDataService';
import { useToast, ToastContainer } from '../common/Toast';


const VendorForm = ({ vendor, onSave, onCancel, viewMode = false }) => {
  // Toast notifications
  const [activeTab, setActiveTab] = useState('basic');

  const tabOrder = ['basic', 'contact', 'business', 'compliance', 'performance'];
  const isLastTab = activeTab === tabOrder[tabOrder.length - 1];
  const isFirstTab = activeTab === tabOrder[0];
  const actionBtnStyle = {
    fontSize: '13px',
    padding: '8px 16px',
    height: '36px',
    minWidth: '110px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px'
  };

  const { toasts, success, error: showError, removeToast } = useToast();
  // Helper function to get form control props based on view mode
  const getFormControlProps = (type = 'text') => {
    const baseStyle = {
      borderRadius: '6px',
      border: '1px solid #d1d5db',
      padding: '8px 12px',
      fontSize: '14px',
      width: '100%',
      boxSizing: 'border-box',
      fontFamily: 'Inter, sans-serif'
    };

    if (viewMode) {
      return {
        readOnly: true,
        disabled: true,
        className: 'form-control',
        style: {
          ...baseStyle,
          backgroundColor: '#f3f4f6',
          color: '#1f2937',
          cursor: 'not-allowed'
        }
      };
    }

    return {
      className: 'form-control',
      style: baseStyle
    };
  };

  const [formData, setFormData] = useState({
    // Basic Information
    vendorId: '', // Will be auto-generated by backend
    name: '',
    legalName: '',
    tradingName: '',
    vendorType: 'Supplier',
    industry: '',
    sector: '',
    
    // Business Registration
    businessRegistration: {
      taxId: '',
      registrationNumber: '',
      legalEntityType: 'Corporation',
      incorporationDate: '',
      incorporationCountry: ''
    },
    
    // Primary Contact
    primaryContact: {
      name: '',
      title: '',
      email: '',
      phone: '',
      mobile: ''
    },
    
    // Addresses
    addresses: {
      headquarters: {
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: ''
      },
      billing: {
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: ''
      },
      shipping: {
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: ''
      }
    },
    
    // Communication
    communication: {
      website: '',
      email: '',
      phone: '',
      fax: '',
      preferredMethod: 'Email'
    },
    
    // Business Details
    businessDetails: {
      creditRating: 'Not Rated',
      paymentTerms: 'Net 30',
      currency: 'USD',
      taxExempt: false,
      taxExemptionNumber: ''
    },
    
    // Banking Information
    banking: {
      bankName: '',
      accountNumber: '',
      routingNumber: '',
      swiftCode: '',
      iban: ''
    },
    
    // Compliance
    compliance: {
      insurance: {
        provider: '',
        policyNumber: '',
        expiryDate: '',
        coverageAmount: ''
      },
      certifications: [],
      complianceStatus: 'Under Review'
    },
    
    // Performance
    performance: {
      overallRating: 3,
      qualityRating: 3,
      deliveryRating: 3,
      serviceRating: 3,
      lastPerformanceReview: '',
      notes: ''
    },
    
    // Service Categories
    serviceCategories: [],
    
    // Risk Assessment
    riskAssessment: {
      riskLevel: 'Medium',
      riskFactors: [],
      lastRiskReview: ''
    },
    
    // Portal Access
    portalAccess: {
      hasAccess: false,
      username: '',
      lastLogin: '',
      accessLevel: 'Basic'
    },
    
    // Status
    status: 'Active'
  });

  // Master data state
  const [masterData, setMasterData] = useState({
    paymentTerms: [],
    currencies: []
  });
  const [loadingMasterData, setLoadingMasterData] = useState(false);
  
  // Test and save state
  const [isSaving, setIsSaving] = useState(false);
  const [testDataLoaded, setTestDataLoaded] = useState(false);

  useEffect(() => {
    if (vendor) {
      setFormData({
        ...formData,
        ...vendor,
        businessRegistration: { ...formData.businessRegistration, ...vendor.businessRegistration },
        primaryContact: { ...formData.primaryContact, ...vendor.primaryContact },
        addresses: { ...formData.addresses, ...vendor.addresses },
        communication: { ...formData.communication, ...vendor.communication },
        businessDetails: { ...formData.businessDetails, ...vendor.businessDetails },
        banking: { ...formData.banking, ...vendor.banking },
        compliance: { ...formData.compliance, ...vendor.compliance },
        performance: { ...formData.performance, ...vendor.performance },
        riskAssessment: { ...formData.riskAssessment, ...vendor.riskAssessment },
        portalAccess: { ...formData.portalAccess, ...vendor.portalAccess }
      });
    }
  }, [vendor]);

  // Load master data on component mount
  useEffect(() => {
    loadMasterData();
  }, []);

  // Load master data from API
  const loadMasterData = async () => {
    try {
      setLoadingMasterData(true);
      console.log('Loading master data for vendor form...');
      const response = await MasterDataService.getContractDropdowns();
      const dropdownData = response.data;
      
      setMasterData({
        paymentTerms: dropdownData.PaymentTerms?.map(item => ({
          value: item.code,
          label: item.label
        })) || [],
        currencies: dropdownData.Currency?.map(item => ({
          value: item.code,
          label: item.label
        })) || []
      });
      
      console.log('Master data loaded for vendor form:', {
        paymentTerms: dropdownData.PaymentTerms?.length || 0,
        currencies: dropdownData.Currency?.length || 0
      });
    } catch (error) {
      console.error('Error loading master data for vendor form:', error);
      // Set fallback data
      setMasterData({
        paymentTerms: [
          { value: 'Net 30', label: 'Net 30' },
          { value: 'Net 15', label: 'Net 15' },
          { value: 'Net 45', label: 'Net 45' },
          { value: 'Net 60', label: 'Net 60' },
          { value: '2/10 Net 30', label: '2/10 Net 30' },
          { value: 'Due on Receipt', label: 'Due on Receipt' },
          { value: 'COD', label: 'COD' }
        ],
        currencies: [
          { value: 'USD', label: 'USD' },
          { value: 'EUR', label: 'EUR' },
          { value: 'GBP', label: 'GBP' },
          { value: 'INR', label: 'INR' },
          { value: 'CAD', label: 'CAD' },
          { value: 'AUD', label: 'AUD' }
        ]
      });
    } finally {
      setLoadingMasterData(false);
    }
  };

  // Generate comprehensive test data
  const generateTestData = () => {
    const testData = {
      // Basic Info
      vendorId: `TEMP-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, // Temporary ID for validation, will be replaced by pre-save middleware
      name: `TechCorp Solutions Inc. - ${Date.now()}`,
      legalName: 'TechCorp Solutions Incorporated',
      tradingName: 'TechCorp',
      vendorType: 'Service Provider',
      industry: 'Technology Services',
      sector: 'Software Development',
      status: 'Active',
      
      // Contact Info
      primaryContact: {
        name: 'Sarah Johnson',
        title: 'Business Development Manager',
        email: 'sarah.johnson@techcorp.com',
        phone: '',
        mobile: ''
      },
      communication: {
        website: 'https://www.techcorp.com',
        email: 'info@techcorp.com',
        phone: '',
        preferredMethod: 'Email'
      },
      
      // Business Info
      businessRegistration: {
        taxId: '12-3456789',
        registrationNumber: 'REG-2024-001',
        legalEntityType: 'Corporation',
        incorporationDate: '2020-01-15',
        incorporationCountry: 'United States'
      },
      businessDetails: {
        creditRating: 'A',
        paymentTerms: 'Net 30',
        currency: 'USD',
        taxExempt: false,
        taxExemptionNumber: ''
      },
      
      // Addresses
      addresses: {
        headquarters: {
          street: '123 Tech Street',
          city: 'San Francisco',
          state: 'California',
          postalCode: '94105',
          country: 'United States'
        },
        billing: {
          street: '123 Tech Street',
          city: 'San Francisco',
          state: 'California',
          postalCode: '94105',
          country: 'United States'
        },
        shipping: {
          street: '123 Tech Street',
          city: 'San Francisco',
          state: 'California',
          postalCode: '94105',
          country: 'United States'
        }
      },
      
      // Banking
      banking: {
        bankName: 'First National Bank',
        accountNumber: '1234567890',
        routingNumber: '021000021',
        swiftCode: 'CHASUS33',
        iban: ''
      },
      
      // Compliance Info
      compliance: {
        insurance: {
          provider: 'Global Insurance Co.',
          policyNumber: 'POL-2024-789',
          expiryDate: '2025-12-31',
          coverageAmount: 2000000
        },
        certifications: [],
        complianceStatus: 'Compliant'
      },
      
      // Performance Info
      performance: {
        overallRating: 4.5,
        qualityRating: 4.7,
        deliveryRating: 4.3,
        serviceRating: 4.6,
        lastPerformanceReview: '2024-01-15',
        notes: 'Excellent vendor with consistent quality delivery and responsive customer service. Highly recommended for technology projects.'
      },
      
      // Service Categories
      serviceCategories: [
        {
          category: 'Software Development',
          subcategory: 'Web Applications',
          description: 'Custom web application development services'
        }
      ],
      
      // Risk Assessment
      riskAssessment: {
        riskLevel: 'Low',
        riskFactors: [],
        lastRiskReview: '2024-01-15'
      },
      
      // Portal Access
      portalAccess: {
        hasAccess: true,
        username: 'techcorp_user',
        lastLogin: '2024-01-15',
        accessLevel: 'Standard'
      }
    };
    
    return testData;
  };

  // Load test data into form
  const loadTestData = () => {
    const testData = generateTestData();
    console.log('Generated test data:', testData);
    console.log('Phone fields in test data:', {
      primaryContactPhone: testData.primaryContact?.phone,
      primaryContactMobile: testData.primaryContact?.mobile,
      communicationPhone: testData.communication?.phone
    });
    
    setFormData(testData);
    setTestDataLoaded(true);
    success('Test data loaded successfully! All form fields have been populated with sample data.');
  };

  // Clear test data
  const clearTestData = () => {
    setFormData({
      // Basic Information
      vendorId: '', // Will be auto-generated by backend
      name: '',
      vendorType: 'Supplier',
      industry: '',
      status: 'Active',
      
      // Contact Information
      primaryContact: {
        name: '',
        title: '',
        email: '',
        phone: ''
      },
      communication: {
        website: '',
        preferredMethod: 'Email'
      },
      
      // Business Information
      businessRegistration: {
        taxId: '',
        registrationNumber: '',
        legalEntityType: 'Corporation'
      },
      businessDetails: {
        creditRating: 'Not Rated',
        paymentTerms: '',
        currency: ''
      },
      
      // Compliance Information
      compliance: {
        insurance: {
          provider: '',
          policyNumber: '',
          expiryDate: '',
          coverageAmount: ''
        }
      },
      
      // Performance Information
      performance: {
        overallRating: '',
        qualityRating: '',
        deliveryRating: '',
        serviceRating: '',
        notes: ''
      }
    });
    setTestDataLoaded(false);
    success('Form cleared successfully!');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child, grandchild] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: grandchild ? {
            ...prev[parent][child],
            [grandchild]: type === 'checkbox' ? checked : value
          } : (type === 'checkbox' ? checked : value)
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSaving) {
      console.log('Already saving, ignoring duplicate submission');
      return; // Prevent double submission
    }
    
    try {
      // Clean and prepare the data for submission
      const cleanedData = {
        ...formData,
        // Ensure required fields are present
        name: formData.name?.trim(),
        vendorType: formData.vendorType,
        
        // Clean up nested objects - remove empty strings and undefined values
        businessRegistration: {
          ...formData.businessRegistration,
          taxId: formData.businessRegistration?.taxId?.trim() || undefined,
          registrationNumber: formData.businessRegistration?.registrationNumber?.trim() || undefined,
          incorporationDate: formData.businessRegistration?.incorporationDate || undefined,
          incorporationCountry: formData.businessRegistration?.incorporationCountry?.trim() || undefined
        },
        
        primaryContact: {
          ...formData.primaryContact,
          name: formData.primaryContact?.name?.trim() || undefined,
          title: formData.primaryContact?.title?.trim() || undefined,
          email: formData.primaryContact?.email?.trim() || undefined,
          phone: formData.primaryContact?.phone?.trim() || undefined,
          mobile: formData.primaryContact?.mobile?.trim() || undefined
        },
        
        communication: {
          ...formData.communication,
          website: formData.communication?.website?.trim() || undefined,
          email: formData.communication?.email?.trim() || undefined,
          phone: formData.communication?.phone?.trim() || undefined,
          fax: formData.communication?.fax?.trim() || undefined
        },
        
        businessDetails: {
          ...formData.businessDetails,
          paymentTerms: formData.businessDetails?.paymentTerms?.trim() || undefined,
          currency: formData.businessDetails?.currency?.trim() || undefined,
          taxExemptionNumber: formData.businessDetails?.taxExemptionNumber?.trim() || undefined
        },
        
        compliance: {
          ...formData.compliance,
          insurance: {
            ...formData.compliance?.insurance,
            provider: formData.compliance?.insurance?.provider?.trim() || undefined,
            policyNumber: formData.compliance?.insurance?.policyNumber?.trim() || undefined,
            coverageAmount: formData.compliance?.insurance?.coverageAmount ? 
              (typeof formData.compliance.insurance.coverageAmount === 'string' 
                ? parseFloat(formData.compliance.insurance.coverageAmount.replace(/[^0-9.-]/g, ''))
                : parseFloat(formData.compliance.insurance.coverageAmount)) : undefined
          }
        },
        
        performance: {
          ...formData.performance,
          overallRating: formData.performance?.overallRating ? parseFloat(formData.performance.overallRating) : undefined,
          qualityRating: formData.performance?.qualityRating ? parseFloat(formData.performance.qualityRating) : undefined,
          deliveryRating: formData.performance?.deliveryRating ? parseFloat(formData.performance.deliveryRating) : undefined,
          serviceRating: formData.performance?.serviceRating ? parseFloat(formData.performance.serviceRating) : undefined,
          notes: formData.performance?.notes?.trim() || undefined
        }
      };
      
      // Remove undefined values from nested objects
      Object.keys(cleanedData).forEach(key => {
        if (typeof cleanedData[key] === 'object' && cleanedData[key] !== null) {
          Object.keys(cleanedData[key]).forEach(subKey => {
            if (cleanedData[key][subKey] === undefined || cleanedData[key][subKey] === '') {
              delete cleanedData[key][subKey];
            }
          });
        }
      });
      
      console.log('Saving cleaned vendor data:', cleanedData);
      console.log('Vendor ID being sent:', cleanedData.vendorId);
      console.log('Company context should be set by backend middleware');
      
    delete cleanedData.vendorId;
    delete cleanedData._id;

      // Call parent handler ONLY
    if (onSave) {
      await onSave(cleanedData);
    }
 
    } catch (error) {
      console.error('Error saving vendor:', error);
      console.error('Error type:', typeof error);
      console.error('Error keys:', Object.keys(error));
      
      // Handle both original AxiosError and transformed error from VendorService
      let errorMessage = error.message || 'Failed to save vendor. Please try again.';
      let validationErrors = null;
      
      // Check if error has the transformed structure from VendorService.handleError()
      if (error.data && error.status) {
        console.error('Transformed error structure:', error);
        errorMessage = error.message || 'Validation failed';
        validationErrors = error.data?.errors;
      }
      // Check if error has the original AxiosError structure
      else if (error.response) {
        console.error('Original AxiosError structure:', error.response);
        errorMessage = error.response.data?.message || error.message || 'Validation failed';
        validationErrors = error.response.data?.errors;
      }
      // Check if error has nested data structure
      else if (error.data) {
        console.error('Nested error structure:', error.data);
        errorMessage = error.data.message || error.message || 'Validation failed';
        validationErrors = error.data.errors;
      }
      
      console.error('Final error message:', errorMessage);
      console.error('Validation errors found:', validationErrors);
      
      // If it's a validation error, show detailed field-specific errors
      if (validationErrors && Array.isArray(validationErrors)) {
        console.log('Detailed validation errors:', validationErrors);
        
        // Format validation errors for display
        const errorDetails = validationErrors.map(err => {
          const field = err.path || err.param || err.field || 'Unknown field';
          const message = err.msg || err.message || 'Invalid value';
          return `${field}: ${message}`;
        });
        
        errorMessage = `Validation failed:\n${errorDetails.join('\n')}`;
        
        // Also show individual errors as separate toasts for better visibility
        validationErrors.forEach((err, index) => {
          setTimeout(() => {
            const field = err.path || err.param || err.field || 'Unknown field';
            const message = err.msg || err.message || 'Invalid value';
            showError(`${field}: ${message}`);
          }, index * 100); // Stagger the toasts
        });
      }
      
      // Show the main error message
      showError(`Error: ${errorMessage}`);
      
      // Log the full error for debugging
      console.error('Full error object for debugging:', {
        originalError: error,
        message: error.message,
        status: error.status || error.response?.status,
        statusText: error.response?.statusText,
        data: error.data || error.response?.data,
        response: error.response
      });
    } finally {
      setIsSaving(false);
    }
  };
  const isStepValid = () => {
    switch (activeTab) {
      case 'basic':
        return (
          formData.name.trim() !== '' &&
          formData.vendorType !== ''
        );

      case 'contact':
        return (
          formData.primaryContact.name.trim() !== '' &&
          formData.primaryContact.email.trim() !== ''
        );

      case 'business':
        return (
          formData.businessDetails.paymentTerms &&
          formData.businessDetails.currency
        );

      case 'compliance':
        return true; // optional

      case 'performance':
        return true;

      default:
        return false;
      }
    };
    const goNext = () => {
      if (!isStepValid()) {
       showError('Please complete all required fields before proceeding.');
       return;
     }

    const currentIndex = tabOrder.indexOf(activeTab);
    setActiveTab(tabOrder[currentIndex + 1]);
    };

    const goBack = () => {
      const currentIndex = tabOrder.indexOf(activeTab);
      setActiveTab(tabOrder[currentIndex - 1]);
    };


  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: Building2 },
    { id: 'contact', label: 'Contact', icon: Phone },
    { id: 'business', label: 'Business', icon: CreditCard },
    { id: 'compliance', label: 'Compliance', icon: Shield },
    { id: 'performance', label: 'Performance', icon: Star }
  ];

  const renderBasicInfo = () => (
    <div className="form-row" style={{ display: 'flex', gap: '12px', width: '100%', flexWrap: 'wrap' }}>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label required" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Vendor Name
          </label>
          <input
            type="text"
            name="name"
            {...getFormControlProps('text')}
            value={formData.name}
            onChange={viewMode ? undefined : handleChange}
            required={!viewMode}
            placeholder="Enter vendor name"
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Vendor Type
          </label>
          <select
            name="vendorType"
            {...getFormControlProps('select')}
            value={formData.vendorType}
            onChange={viewMode ? undefined : handleChange}
            required={!viewMode}
          >
            <option value="Supplier">Supplier</option>
            <option value="Service Provider">Service Provider</option>
            <option value="Contractor">Contractor</option>
            <option value="Consultant">Consultant</option>
            <option value="Manufacturer">Manufacturer</option>
            <option value="Distributor">Distributor</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Industry
          </label>
          <input
            type="text"
            name="industry"
            {...getFormControlProps('text')}
            value={formData.industry}
            onChange={viewMode ? undefined : handleChange}
            placeholder="e.g., Technology, Manufacturing"
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Status
          </label>
          <select
            name="status"
            {...getFormControlProps('select')}
            value={formData.status}
            onChange={viewMode ? undefined : handleChange}
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Suspended">Suspended</option>
            <option value="Pending Approval">Pending Approval</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderContactInfo = () => (
    <div className="form-row" style={{ display: 'flex', gap: '12px', width: '100%', flexWrap: 'wrap' }}>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Contact Name
          </label>
          <input
            type="text"
            name="primaryContact.name"
            {...getFormControlProps('text')}
            value={formData.primaryContact.name}
            onChange={viewMode ? undefined : handleChange}
            placeholder="Enter contact person name"
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Contact Title
          </label>
          <input
            type="text"
            name="primaryContact.title"
            {...getFormControlProps('text')}
            value={formData.primaryContact.title}
            onChange={viewMode ? undefined : handleChange}
            placeholder="e.g., Sales Manager, CEO"
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Email
          </label>
          <input
            type="email"
            name="primaryContact.email"
            {...getFormControlProps('email')}
            value={formData.primaryContact.email}
            onChange={viewMode ? undefined : handleChange}
            placeholder="contact@vendor.com"
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Phone
          </label>
          <input
            type="tel"
            name="primaryContact.phone"
            {...getFormControlProps('tel')}
            value={formData.primaryContact.phone}
            onChange={viewMode ? undefined : handleChange}
            placeholder="+1 (555) 123-4567"
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Website
          </label>
          <input
            type="url"
            name="communication.website"
            {...getFormControlProps('url')}
            value={formData.communication.website}
            onChange={viewMode ? undefined : handleChange}
            placeholder="https://www.vendor.com"
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Preferred Method
          </label>
          <select
            name="communication.preferredMethod"
            {...getFormControlProps('select')}
            value={formData.communication.preferredMethod}
            onChange={viewMode ? undefined : handleChange}
          >
            <option value="Email">Email</option>
            <option value="Phone">Phone</option>
            <option value="Fax">Fax</option>
            <option value="Mail">Mail</option>
            <option value="Portal">Portal</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderBusinessInfo = () => (
    <div className="form-row" style={{ display: 'flex', gap: '12px', width: '100%', flexWrap: 'wrap' }}>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Tax ID
          </label>
          <input
            type="text"
            name="businessRegistration.taxId"
            {...getFormControlProps('text')}
            value={formData.businessRegistration.taxId}
            onChange={viewMode ? undefined : handleChange}
            placeholder="Enter tax identification number"
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Registration Number
          </label>
          <input
            type="text"
            name="businessRegistration.registrationNumber"
            {...getFormControlProps('text')}
            value={formData.businessRegistration.registrationNumber}
            onChange={viewMode ? undefined : handleChange}
            placeholder="Enter business registration number"
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Legal Entity Type
          </label>
          <select
            name="businessRegistration.legalEntityType"
            {...getFormControlProps('select')}
            value={formData.businessRegistration.legalEntityType}
            onChange={viewMode ? undefined : handleChange}
          >
            <option value="Corporation">Corporation</option>
            <option value="Partnership">Partnership</option>
            <option value="Sole Proprietorship">Sole Proprietorship</option>
            <option value="LLC">LLC</option>
            <option value="LLP">LLP</option>
            <option value="Government">Government</option>
            <option value="Non-Profit">Non-Profit</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Credit Rating
          </label>
          <select
            name="businessDetails.creditRating"
            {...getFormControlProps('select')}
            value={formData.businessDetails.creditRating}
            onChange={viewMode ? undefined : handleChange}
          >
            <option value="AAA">AAA</option>
            <option value="AA">AA</option>
            <option value="A">A</option>
            <option value="BBB">BBB</option>
            <option value="BB">BB</option>
            <option value="B">B</option>
            <option value="CCC">CCC</option>
            <option value="CC">CC</option>
            <option value="C">C</option>
            <option value="D">D</option>
            <option value="Not Rated">Not Rated</option>
          </select>
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Payment Terms
          </label>
          <select
            name="businessDetails.paymentTerms"
            {...getFormControlProps('select')}
            value={formData.businessDetails.paymentTerms}
            onChange={viewMode ? undefined : handleChange}
          >
            <option value="">Select Payment Terms</option>
            {loadingMasterData ? (
              <option disabled>Loading...</option>
            ) : (
              masterData.paymentTerms.map(term => (
                <option key={term.value} value={term.value}>{term.label}</option>
              ))
            )}
          </select>
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Currency
          </label>
          <select
            name="businessDetails.currency"
            {...getFormControlProps('select')}
            value={formData.businessDetails.currency}
            onChange={viewMode ? undefined : handleChange}
          >
            <option value="">Select Currency</option>
            {loadingMasterData ? (
              <option disabled>Loading...</option>
            ) : (
              masterData.currencies.map(currency => (
                <option key={currency.value} value={currency.value}>{currency.label}</option>
              ))
            )}
          </select>
        </div>
      </div>
    </div>
  );

  const renderComplianceInfo = () => (
    <div className="form-row" style={{ display: 'flex', gap: '12px', width: '100%', flexWrap: 'wrap' }}>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Insurance Provider
          </label>
          <input
            type="text"
            name="compliance.insurance.provider"
            {...getFormControlProps('text')}
            value={formData.compliance.insurance.provider}
            onChange={viewMode ? undefined : handleChange}
            placeholder="Enter insurance provider name"
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Policy Number
          </label>
          <input
            type="text"
            name="compliance.insurance.policyNumber"
            {...getFormControlProps('text')}
            value={formData.compliance.insurance.policyNumber}
            onChange={viewMode ? undefined : handleChange}
            placeholder="Enter policy number"
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Expiry Date
          </label>
          <input
            type="date"
            name="compliance.insurance.expiryDate"
            {...getFormControlProps('date')}
            value={formData.compliance.insurance.expiryDate}
            onChange={viewMode ? undefined : handleChange}
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Coverage Amount
          </label>
          <input
            type="text"
            name="compliance.insurance.coverageAmount"
            {...getFormControlProps('text')}
            value={formData.compliance.insurance.coverageAmount}
            onChange={viewMode ? undefined : handleChange}
            placeholder="Enter coverage amount"
          />
        </div>
      </div>
    </div>
  );

  const renderPerformanceInfo = () => (
    <div className="form-row" style={{ display: 'flex', gap: '12px', width: '100%', flexWrap: 'wrap' }}>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Overall Rating
          </label>
          <input
            type="number"
            min="1"
            max="5"
            step="0.1"
            name="performance.overallRating"
            {...getFormControlProps('number')}
            value={formData.performance.overallRating}
            onChange={viewMode ? undefined : handleChange}
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Quality Rating
          </label>
          <input
            type="number"
            min="1"
            max="5"
            step="0.1"
            name="performance.qualityRating"
            {...getFormControlProps('number')}
            value={formData.performance.qualityRating}
            onChange={viewMode ? undefined : handleChange}
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Delivery Rating
          </label>
          <input
            type="number"
            min="1"
            max="5"
            step="0.1"
            name="performance.deliveryRating"
            {...getFormControlProps('number')}
            value={formData.performance.deliveryRating}
            onChange={viewMode ? undefined : handleChange}
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Service Rating
          </label>
          <input
            type="number"
            min="1"
            max="5"
            step="0.1"
            name="performance.serviceRating"
            {...getFormControlProps('number')}
            value={formData.performance.serviceRating}
            onChange={viewMode ? undefined : handleChange}
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '100%' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Performance Notes
          </label>
          <textarea
            name="performance.notes"
            {...getFormControlProps('textarea')}
            rows="4"
            value={formData.performance.notes}
            onChange={viewMode ? undefined : handleChange}
            placeholder="Enter performance notes and comments"
          />
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return renderBasicInfo();
      case 'contact':
        return renderContactInfo();
      case 'business':
        return renderBusinessInfo();
      case 'compliance':
        return renderComplianceInfo();
      case 'performance':
        return renderPerformanceInfo();
      default:
        return renderBasicInfo();
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Tab Navigation */}
      <div className="tabs-modern">
        <ul className="tabs-modern-list">
          {tabs.map(tab => (
            <li key={tab.id} className="tab-modern-item">
              <button
                type="button"
                className={`tab-modern-link ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <tab.icon size={16} className="tab-modern-icon" />
                {tab.label}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {renderTabContent()}
      </div>

      {/* Form Actions */}
      <div className="modal-footer"
          style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          paddingTop: '16px'
      }}>
        {!viewMode && (
          <>
            {/* Test Data Buttons */}
            <div className="d-flex gap-2 me-auto">
              <button
                type="button"
                className="btn btn-outline-info btn-sm d-flex align-items-center gap-2"
                onClick={loadTestData}
                disabled={isSaving}
                style={{
                  ...actionBtnStyle,
                  borderStyle:'dashed'
                }}
              >
                <TestTube size={14} />
                Load Test Data
              </button>
              {testDataLoaded && (
                <button
                  type="button"
                  className="btn btn-outline-warning btn-sm d-flex align-items-center gap-2"
                  onClick={clearTestData}
                  disabled={isSaving}
                  style={actionBtnStyle}
                >
                  <X size={14} />
                  Clear
                </button>
              )}
            </div>
            
            <button
              type="button"
              className="btn-outline-cam"
              onClick={onCancel}
              disabled={isSaving}
              style={actionBtnStyle}
            >
              Cancel
            </button>
            <div className="modal-footer">
            {!viewMode && (
              <>
              {/* Back button */}
              {!isFirstTab && (
                <button
                type="button"
                className="btn-outline-cam"
                onClick={goBack}
                style={actionBtnStyle}
              >
                Back
            </button>
           )}

              {/* Next OR Submit */}
              {!isLastTab ? (
                <button
                  type="button"
                  className="btn-cam"
                  onClick={goNext}
                  disabled={isSaving}
                  style={actionBtnStyle}
              >
              Next
             </button>
          ) : (
          <button
            type="submit"
            className="btn-cam"
            disabled={isSaving}
            style={actionBtnStyle}
          >
            {isSaving ? 'Saving...' : vendor ? 'Update Vendor' : 'Create Vendor'}
          </button>
        )}
      </>
    )}

    {viewMode && (
      <button type="button" 
        className="btn-outline-cam" 
        onClick={onCancel}
        style={actionBtnStyle}>
        Close
      </button>
    )}
  </div>

          </>
        )}
        {viewMode && (
          <button
            type="button"
            className="btn-outline-cam"
            onClick={onCancel}
            style={actionBtnStyle}
          >
            Close
          </button>
        )}
      </div>
      
      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </form>
  );
};

export default VendorForm;
